// src/pages/UnsoldTicketAppraisal/components/AppraisalList.tsx
// Bảng danh sách phiếu thu hồi vé ế (TicketRecall, Status=Confirmed/Approved/Rejected) chờ/đã duyệt
// (mục 22/23, REQUIRE_FIXBUGS.md — thay thế luồng cũ dựa trên TicketReturnReconciliation).

import React from 'react';
import { Button } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import DataTableShell, { getSttNumber } from 'Components/Common/DataTableShell';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { AgentMap, getAgentCode, getAgentName } from 'helpers/agentLookup';
import {
  TICKET_RECALL_STATUS,
  TICKET_RECALL_STATUS_COLORS,
  TICKET_RECALL_STATUS_LABELS,
  TicketRecall,
  TicketRecallPagination,
} from 'pages/TicketRecall/types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('unsold_ticket_appraisal');

interface AppraisalListProps {
  items: TicketRecall[];
  pagination: TicketRecallPagination;
  agentMap: AgentMap;
  loading: boolean;
  onViewDetail: (item: TicketRecall) => void;
  onApprove: (item: TicketRecall) => void;
  onReject: (item: TicketRecall) => void;
}

const APPRAISAL_ACTION_COLUMN_CODES = ['button-detail', 'button-approve', 'button-reject'];

const formatNumber = (value?: number | null) =>
  typeof value === 'number' ? value.toLocaleString('vi-VN') : '0';

const AppraisalList: React.FC<AppraisalListProps> = ({
  items,
  pagination,
  agentMap,
  loading,
  onViewDetail,
  onApprove,
  onReject,
}) => {
  const location = useLocation();
  const canShowActionColumn = PageActionPermission.canDisplayAny(
    location.pathname,
    APPRAISAL_ACTION_COLUMN_CODES,
  );
  const columnCount = canShowActionColumn ? 7 : 6;

  return (
    <DataTableShell
      headerRow={
        <tr>
          <th className="text-center" style={{ width: 60 }}>{st('stt_column')}</th>
          <th>{st('ma_phieu_thu_hoi_column')}</th>
          <th>{st('ke_hoach_phat_hanh_column')}</th>
          <th>{st('nguon_thu_hoi_column')}</th>
          <th className="text-end">{st('so_luong_ve_column')}</th>
          <th>{st('trang_thai_column')}</th>
          {canShowActionColumn && (
            <th style={{ width: 190 }} className="text-center">
              {st('thao_tac_column')}
            </th>
          )}
        </tr>
      }
    >
      {loading ? (
        Array.from({ length: 5 }).map((_, row) => (
          <tr key={row}>
            {Array.from({ length: columnCount }).map((__, col) => (
              <td key={col}>
                <div className="placeholder-glow">
                  <span className="placeholder col-8"></span>
                </div>
              </td>
            ))}
          </tr>
        ))
      ) : items.length === 0 ? (
        <tr>
          <td colSpan={columnCount} className="text-center text-muted py-5">
            <div className="mb-2">
              <i className="ri-file-list-3-line fs-1 text-muted"></i>
            </div>
            {st('khong_co_phieu_thu_hoi_nao_text')}
          </td>
        </tr>
      ) : (
        items.map((item, index) => {
          const isPendingApproval = Number(item.status) === TICKET_RECALL_STATUS.Confirmed;
          const color =
            item.statusBadgeColor || TICKET_RECALL_STATUS_COLORS[String(item.status)] || 'secondary';
          const label =
            item.statusName || TICKET_RECALL_STATUS_LABELS[String(item.status)] || st('khong_ro_text');

          return (
            <tr key={item.id}>
              <td className="text-center">{getSttNumber(pagination.currentPage, pagination.pageSize, index)}</td>
              <td>
                <div className="fw-medium text-primary">{item.recallCode}</div>
                <small className="text-muted">
                  {item.confirmedAt ? moment(item.confirmedAt).format('DD/MM/YYYY HH:mm') : ''}
                </small>
              </td>
              <td>{item.planCode || item.issuancePlanId}</td>
              <td>
                {item.agentId ? (
                  <>
                    <div className="fw-medium">{getAgentName(agentMap, item.agentId)}</div>
                    <small className="text-muted">{getAgentCode(agentMap, item.agentId)}</small>
                  </>
                ) : (
                  <span className="text-muted">{st('kho_tong_text')}</span>
                )}
              </td>
              <td className="text-end">{formatNumber(item.totalQuantity)}</td>
              <td>
                <span className={`badge bg-${color}-subtle text-${color} text-uppercase`}>
                  {label}
                </span>
              </td>
              {canShowActionColumn && (
                <td className="text-center">
                  <div className="d-flex justify-content-center gap-1 flex-nowrap">
                    {PageActionPermission.canDisplay(location.pathname, 'button-detail') && (
                      <Button
                        color="info"
                        outline
                        size="sm"
                        className="btn-icon"
                        title={st('doi_soat_xem_chi_tiet_title')}
                        data-action-code="button-detail"
                        data-action-name="Đối soát phiếu thu hồi vé ế"
                        onClick={() => onViewDetail(item)}
                      >
                        <i className="ri-file-search-line"></i>
                      </Button>
                    )}
                    {isPendingApproval && (
                      <>
                        {PageActionPermission.canDisplay(location.pathname, 'button-approve') && (
                          <Button
                            color="success"
                            outline
                            size="sm"
                            data-action-code="button-approve"
                            data-action-name="Duyệt phiếu thu hồi vé ế"
                            onClick={() => onApprove(item)}
                          >
                            <i className="ri-check-double-line align-bottom me-1"></i>
                            {st('duyet_button')}
                          </Button>
                        )}
                        {PageActionPermission.canDisplay(location.pathname, 'button-reject') && (
                          <Button
                            color="danger"
                            outline
                            size="sm"
                            data-action-code="button-reject"
                            data-action-name="Từ chối phiếu thu hồi vé ế"
                            onClick={() => onReject(item)}
                          >
                            <i className="ri-close-line align-bottom me-1"></i>
                            {st('tu_choi_button')}
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </td>
              )}
            </tr>
          );
        })
      )}
    </DataTableShell>
  );
};

export default AppraisalList;
